/**
 * Currency CODE → the symbol to print (`"USD"` → `"$"`). Pure: no store, so the selectors, the
 * plain functions and the hooks all resolve a symbol the same way.
 *
 * ⚠️ The library is IMPORTED, never read off `window.RL` (CLAUDE.md, audit check K). The web app
 * used to read `window?.RL?.common?.curr`; that global IS this module, so this is the same table
 * with no bootstrap dependency, and mobile reaches it the same way.
 */
import common from "@dotpe/rista-library/lib/common.js";

import { CURRENCY_CONSTANTS, INR } from "./format";

/**
 * Turns the library's HTML character references into text: `&#x20b9` → `₹`.
 *
 * The table it decodes is rista-library's `common.curr`: 118 entries, EVERY one a run of hex
 * references with no trailing semicolon (`&#x4c&#x65&#x6b` = "Lek"), which is why this handles hex
 * and the optional semicolon and nothing else. Text with no reference in it (`"₹"`) comes back
 * untouched. `currency.test.ts` checks every entry of the installed table rather than trusting this.
 */
const decodeHtmlEntities = (value: string): string =>
  value
    ? value.replace(/&#x([0-9a-fA-F]+);?/g, (_, hex: string) =>
        String.fromCodePoint(parseInt(hex, 16)),
      )
    : "";

/**
 * The symbol to print for a currency code. Three answers, in order: the library's symbol for the
 * code, the code itself (an unknown currency prints as `XYZ100.00`: odd-looking, but honest), and
 * finally the rupee.
 *
 * @example currencySymbolFor("USD")    // "$"
 * @example currencySymbolFor("ALL")    // "Lek"
 * @example currencySymbolFor(undefined) // "₹"
 */
export function currencySymbolFor(currencyCode?: string): string {
  return decodeHtmlEntities(
    (currencyCode && common?.curr?.[currencyCode]) || currencyCode || CURRENCY_CONSTANTS[INR],
  );
}
